/**
 * 5 list pages для Підпорції 1.1 (2026-05-25).
 *
 * MVP: list-only для всіх таблиць. Editor pages — окрема міні-порція
 * (TECHDEBT). Sidebar registration у App.tsx.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import {
  assetGroups,
  depreciationMethods,
  fixedAssets,
  intangibleAssets,
  lowValueAssets,
  assetCommissioning,
  assetWriteOff,
  type AssetGroup,
  type AssetKind,
  type DepreciationMethod,
  type FixedAsset,
  type IntangibleAsset,
  type LowValueAsset,
  type AssetCommissioning,
  type AssetWriteOff,
} from '@/api/assets';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const KIND_UA: Record<AssetKind, string> = {
  FIXED_ASSET: 'ОЗ',
  INTANGIBLE: 'НМА',
  LOW_VALUE: 'МНМА',
};


// ============================== Asset Groups ==========================


export function AssetGroupsPage() {
  const [kind, setKind] = useState<AssetKind | ''>('');
  const list = useQuery({
    queryKey: ['asset-groups', kind],
    queryFn: () => assetGroups.list({ kind: kind || undefined, limit: 500 }),
  });
  const cols = useMemo<ColumnDef<AssetGroup>[]>(
    () => [
      { key: 'code', header: 'Код', width: 140, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      {
        key: 'asset_kind', header: 'Тип', width: 100,
        sortValue: (r) => r.asset_kind ?? '',
        cell: (r) => KIND_UA[r.asset_kind] ?? String(r.asset_kind),
      },
      { key: 'group_number', header: '№', width: 60, align: 'right', sortValue: (r) => r.group_number ?? 0, cell: (r) => r.group_number },
      { key: 'name_uk', header: 'Назва', sortValue: (r) => r.name_uk ?? '', cell: (r) => r.name_uk },
      {
        key: 'min_useful_life_months', header: 'Мін. термін (міс.)',
        width: 160, align: 'right',
        sortValue: (r) => r.min_useful_life_months ?? 0,
        cell: (r) => r.min_useful_life_months,
      },
      { key: 'accounting_account_code', header: 'Рахунок', width: 110, sortValue: (r) => r.accounting_account_code ?? '', cell: (r) => r.accounting_account_code },
      { key: 'depreciation_account_code', header: 'Знос', width: 100, sortValue: (r) => r.depreciation_account_code ?? '', cell: (r) => r.depreciation_account_code },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar" style={{ gap: 12 }}>
        <h2 style={{ margin: 0 }}>Групи активів (ПКУ ст.138.3.3+138.3.4)</h2>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as AssetKind | '')}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Усі</option>
          <option value="FIXED_ASSET">ОЗ (16)</option>
          <option value="INTANGIBLE">НМА (6)</option>
        </select>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<AssetGroup>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="code"
      />
    </>
  );
}


// ============================== Depreciation Methods ==================


export function DepreciationMethodsPage() {
  const list = useQuery({
    queryKey: ['dep-methods'],
    queryFn: () => depreciationMethods.list({ limit: 100 }),
  });
  const cols = useMemo<ColumnDef<DepreciationMethod>[]>(
    () => [
      { key: 'code', header: 'Код', width: 200, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name_uk', header: 'Назва', width: 280, sortValue: (r) => r.name_uk ?? '', cell: (r) => r.name_uk },
      { key: 'description', header: 'Опис', sortValue: (r) => r.description ?? '', cell: (r) => r.description },
      {
        key: 'is_active', header: 'Активний', width: 120,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Методи амортизації (НП(С)БО 7)</h2>
      </div>
      <DataTable<DepreciationMethod>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="code"
      />
    </>
  );
}


// ============================== Fixed Assets ==========================


export function FixedAssetsPage() {
  const list = useQuery({
    queryKey: ['fixed-assets-v2'],
    queryFn: () => fixedAssets.list({ limit: 500 }),
  });
  const cols = useMemo<ColumnDef<FixedAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Первісна', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      {
        key: 'accumulated_depreciation', header: 'Накоп. амор.', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.accumulated_depreciation),
        cell: (r) => fmt2(r.accumulated_depreciation),
      },
      {
        key: 'residual_value', header: 'Залишкова', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.residual_value),
        cell: (r) => fmt2(r.residual_value),
      },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
      { key: 'commissioning_date', header: 'Дата введення', width: 140, sortValue: (r) => r.commissioning_date ?? '', cell: (r) => r.commissioning_date },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Основні засоби (ОЗ)</h2>
      </div>
      <DataTable<FixedAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="inventory_number"
      />
    </>
  );
}


// ============================== Intangible Assets =====================


export function IntangibleAssetsPage() {
  const list = useQuery({
    queryKey: ['intangible-assets-v2'],
    queryFn: () => intangibleAssets.list({ limit: 500 }),
  });
  const cols = useMemo<ColumnDef<IntangibleAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Первісна', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      {
        key: 'residual_value', header: 'Залишкова', width: 140,
        align: 'right',
        sortValue: (r) => Number(r.residual_value),
        cell: (r) => fmt2(r.residual_value),
      },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нематеріальні активи (НМА)</h2>
      </div>
      <DataTable<IntangibleAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="inventory_number"
      />
    </>
  );
}


// ============================== Low Value Assets ======================


export function LowValueAssetsPage() {
  const list = useQuery({
    queryKey: ['low-value-assets'],
    queryFn: () => lowValueAssets.list({ limit: 500 }),
  });
  const cols = useMemo<ColumnDef<LowValueAsset>[]>(
    () => [
      { key: 'inventory_number', header: 'Інв. №', width: 160, sortValue: (r) => r.inventory_number ?? '', cell: (r) => r.inventory_number },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'primary_cost', header: 'Вартість', width: 130,
        align: 'right',
        sortValue: (r) => Number(r.primary_cost),
        cell: (r) => fmt2(r.primary_cost),
      },
      { key: 'quantity', header: 'Кільк.', width: 90, align: 'right', sortValue: (r) => r.quantity ?? 0, cell: (r) => r.quantity },
      { key: 'write_off_method', header: 'Метод списання', width: 160, sortValue: (r) => r.write_off_method ?? '', cell: (r) => r.write_off_method },
      { key: 'track_mode', header: 'Облік', width: 120, sortValue: (r) => r.track_mode ?? '', cell: (r) => r.track_mode },
      { key: 'status', header: 'Статус', width: 130, sortValue: (r) => r.status ?? '', cell: (r) => r.status },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Малоцінка (МНМА &lt; 20K)</h2>
      </div>
      <DataTable<LowValueAsset>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає даних."
        initialSortKey="inventory_number"
      />
    </>
  );
}


// ============================== Asset Commissioning ===================


export function AssetCommissioningPage() {
  const list = useQuery({
    queryKey: ['asset-commissioning'],
    queryFn: () => assetCommissioning.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<AssetCommissioning>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'target_status', header: 'Цільовий статус', width: 160, sortValue: (r) => r.target_status ?? '', cell: (r) => r.target_status },
      {
        key: 'items', header: 'Позицій', width: 110,
        align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Введення в експлуатацію</h2>
      </div>
      <DataTable<AssetCommissioning>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Asset Write-off =======================


export function AssetWriteOffPage() {
  const list = useQuery({
    queryKey: ['asset-write-off'],
    queryFn: () => assetWriteOff.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<AssetWriteOff>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'reason', header: 'Причина', width: 130, sortValue: (r) => r.reason ?? '', cell: (r) => r.reason },
      {
        key: 'items', header: 'Позицій', width: 110,
        align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Списання активів</h2>
      </div>
      <DataTable<AssetWriteOff>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
